import { Suspense, useState, type FC, type ReactNode } from 'react';

import { CmsProvider, ComponentErrorBoundary } from '@eneco/cms';
import type { CmsSlotAdder, ComponentsDefinition } from '@eneco/cms';
import type { CmsAdapter, CmsPage } from '@eneco/cms-adapter';
import { SlotAdderBridge, VisualEditing, createEditAttribute } from '@eneco/cms-sanity';
import { ApplicationProvider } from '@eneco/common/application';
import type { I18n } from '@eneco/common/application';
import { ErrorBoundary } from '@eneco/common/log';
import { CmsGTMProvider, PageViewTracker } from '@eneco/containers';
import type { CmsGTMProviderProps } from '@eneco/containers';
import { DigitalCoreProvider } from '@eneco/dc/provider';
import type { AuthenticationContext } from '@eneco/dc/provider';
import { LinkComponentProvider } from '@eneco/link';
import { Head, Link } from '@eneco/next';
import { SparkyProvider } from '@eneco/sparky/providers';
import { CmsEnhancer } from '@sitecore/common';

import { pageMeta } from './pageMeta';

/**
 * CMS-agnostic render host for a fetched CMS page (Phase E).
 *
 * Sibling to `SitecoreRouteContainer` — deliberately minimal for first paint:
 * it sources everything from the `CmsPage` and depends on NO Sitecore
 * `layoutData`. `CmsEnhancer` wires GTM tracking + NBA feedback per component
 * (see `docs/cms-component-rendering.md`). This lane is env-gated to
 * development/local only (`isSanityEnabled`) plus requires a resolved
 * `CmsPage`, so it never activates on a shared or customer-facing
 * environment — see `libs/cms-sanity/src/is-sanity-enabled.ts`.
 */
export type CmsRouteContainerProps = {
  adapter: CmsAdapter;
  page: CmsPage;
  components: ComponentsDefinition;
  /**
   * The page's own locale set. Passed whole rather than as a bare `locale` because it also feeds the
   * `ApplicationProvider` below, which is what `@eneco/i18n`'s `useFormatter` reads its locale from.
   */
  i18n: I18n;
  businessUnit: CmsGTMProviderProps['businessUnit'];
  /** Overrides the page's own `browserTitle`. Rarely needed — see `pageTitle`. */
  title?: string;
  /** Brand, for Digital Core calls made by blocks in this lane (`GenericForm`). */
  label: AuthenticationContext['label'];
  platformHint?: 'desktop' | 'mobile';
  children: ReactNode;
};

const CmsRouteContainer: FC<CmsRouteContainerProps> = ({
  adapter,
  page,
  components,
  i18n,
  businessUnit,
  label,
  platformHint,
  title,
  children,
}) => {
  // Present only when the page was fetched from the drafts perspective — see
  // `CmsContext.editing`. Everything Visual Editing costs, in markup and in JavaScript,
  // is behind this one check, so a published render is byte-for-byte what it was before.
  const editing = page.context.editing;

  // Set by `SlotAdderBridge` once its lazy module loads, the page is framed in Presentation and
  // its document store exists (`use-slot-adder.ts`). `undefined` until then — including on the
  // server — so the add tiles render nothing before it arrives.
  const [slotAdder, setSlotAdder] = useState<CmsSlotAdder | undefined>(undefined);
  // An adder writes to the document it was made for. A client-side navigation keeps this container
  // and its state, while the bridge hands over the next page's adder only after that page has
  // rendered — so drop the old one in this render, or a click in between would edit the page just left.
  const [adderDocumentId, setAdderDocumentId] = useState(editing?.documentId);
  if (adderDocumentId !== editing?.documentId) {
    setAdderDocumentId(editing?.documentId);
    setSlotAdder(undefined);
  }

  return (
    <ErrorBoundary fallback={<div>Something went wrong rendering this page.</div>}>
      <PageHead page={page} adapter={adapter} title={title} />
      <CmsGTMProvider businessUnit={businessUnit}>
        <SparkyProvider locale={i18n.locale} platformHint={platformHint} theme="default">
          {/*
            `useFormatter` (`@eneco/i18n`) reads its locale from this provider's context, which falls
            back to a `nl-NL` default when nothing mounts it — silently wrong on the BE and GB sites.
            Blocks that format currency, dates or units (`DynamicPriceCard*`, `ModelContractTable`,
            `EnergyStatisticsByRegion`, `WarmthInterruptions`) depend on it being the page's real locale.
          */}
          <ApplicationProvider i18n={i18n} isEditMode={!!editing}>
            {/*
              Guest-only: this lane has no authenticated session and no template on CmsContext, so
              page_view reports without page_type. The provider is here so blocks that call the Digital
              Core (`GenericForm`) can read `label`/`businessUnit`.
            */}
            <DigitalCoreProvider customerContext={{}} label={label} businessUnit={businessUnit}>
              <PageViewTracker enabled={businessUnit === 'nl'} />
              {/*
                Sparky components that render their own link (`TileBar`, `NotificationBox`) take it from
                this provider. Without it they get `@eneco/link`'s bare default, which drops the anchor's
                class, so a TopTasks tile renders as a plain browser link. The Sitecore route
                (`SitecoreRouteContainer`) provides the same component.
              */}
              <LinkComponentProvider component={Link}>
                <CmsProvider
                  adapter={adapter}
                  page={page}
                  components={components}
                  wrapComponent={CmsEnhancer}
                  editAttribute={editing ? createEditAttribute(editing) : undefined}
                  slotAdder={editing ? slotAdder : undefined}>
                  {children}
                  {editing && <VisualEditing editing={editing} />}
                  {editing && (
                    // Its own boundary, not the page-level one above: a lazy-chunk load failure or a
                    // throw during Presentation's handshake must disable this one optional affordance,
                    // not replace the whole draft page with an error screen. `fallback={null}` matches
                    // what this renders anyway on success — a failure here should be invisible.
                    <ComponentErrorBoundary componentName="SlotAdderBridge" fallback={null}>
                      <Suspense fallback={null}>
                        <SlotAdderBridge editing={editing} onReady={setSlotAdder} />
                      </Suspense>
                    </ComponentErrorBoundary>
                  )}
                </CmsProvider>
              </LinkComponentProvider>
            </DigitalCoreProvider>
          </ApplicationProvider>
        </SparkyProvider>
      </CmsGTMProvider>
    </ErrorBoundary>
  );
};

/**
 * The document head, from the page's own SEO fields. Split out so the container stays a layout: this
 * is the only part that reads `root.content` rather than passing it down.
 */
const PageHead: FC<{ page: CmsPage; adapter: CmsAdapter; title?: string }> = ({ page, adapter, title }) => {
  const meta = pageMeta(page, adapter);

  return (
    <Head>
      <title>{title ?? meta.title}</title>
      {meta.description && <meta name="description" content={meta.description} />}
      {/* Absent means the default `index, follow`; only an explicit opt-out is emitted. */}
      {meta.robots && <meta name="robots" content={meta.robots} />}
      {meta.canonical && <link rel="canonical" href={meta.canonical} />}
      {meta.openGraph.title && <meta property="og:title" content={meta.openGraph.title} />}
      {meta.openGraph.description && <meta property="og:description" content={meta.openGraph.description} />}
      {meta.openGraph.image && <meta property="og:image" content={meta.openGraph.image} />}
    </Head>
  );
};

export default CmsRouteContainer;
